import * as React from 'react';
import Divider from '@mui/material/Divider';
import ListItemButton from '@mui/material/ListItemButton';
import Menu from '@mui/material/Menu';
import Typography from '@mui/material/Typography';
import HelpIcon from '@mui/icons-material/HelpOutlineRounded';
import BugReportIcon from '@mui/icons-material/BugReport';
import * as Sentry from '@sentry/react';
import { useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';

import Flex from 'src/components/flex/Flex';
import { ProfileImageRounded } from 'src/components/ProfileImageRounded';
import { type IUser } from 'src/api/users-api';
import { Auth } from 'src/services/api';
import { LogOutIcon, SettingsIcon } from 'src/icons';
import { useAppState } from 'src/AppStateProvider';

type Props = {
    anchorEl: HTMLElement | null;
    setIsProfileMenuOpened: (isOpened: boolean) => void;
    isSidebarCollapsed: boolean;
    loggedInUser: IUser | undefined;
    openTeamSwitcher: () => void;
}

export default function NavigationSidebarMenu({ anchorEl, setIsProfileMenuOpened, loggedInUser, openTeamSwitcher }: Props) {
    const { setHelpSidebarOpened } = useAppState();
    const navigate = useNavigate();

    const handleLogout = () => {
        Auth.logout();
    };

    const handleClickReportABug = async () => {
        const feedback = Sentry.getFeedback();
        const form = await feedback?.createForm();
        form?.appendToDom();
        form?.open();
        setIsProfileMenuOpened(false);
    };

    // NOTE: landlord users that have completed onboarding have a team
    const teamName = loggedInUser?.team?.name || '';
    const canSwitchTeam = Boolean(loggedInUser?.org_user_id);

    return (
        <Menu
            anchorEl={anchorEl}
            open={true}
            onClose={() => setIsProfileMenuOpened(false)}
            anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
            }}
        >
            <Flex px={2} alignItems="center" justifyContent="space-between" gap={2} flexDirection="row">
                <ProfileImageRounded url={null} userName={teamName} width={32} height={32} variant="square" />
                <Flex py={2} gap={2} textAlign="start" justifyContent="space-between" alignItems="center">
                    <Typography fontWeight="600" fontSize={14} lineHeight="120%"
                        color="rgba(66, 64, 58, 1)" className="ellipsisText"
                    >
                        {teamName}
                    </Typography>
                    <Button variant="contained" size="small" disabled={!canSwitchTeam} onClick={canSwitchTeam ? openTeamSwitcher : undefined}>Switch</Button>
                </Flex>
            </Flex>
            <Divider />
            <ListItemButton onClick={() => {
                navigate('/landlord/inner/settings');
                setIsProfileMenuOpened(false);
            }}
            >
                <Flex>
                    <SettingsIcon />
                    <Typography fontSize={14}>Settings</Typography>
                </Flex>
            </ListItemButton>
            <ListItemButton onClick={() => {
                setHelpSidebarOpened(true);
                setIsProfileMenuOpened(false);
            }}
            >
                <Flex>
                    <HelpIcon />
                    <Typography fontSize={14}>Help</Typography>
                </Flex>
            </ListItemButton>
            <ListItemButton onClick={handleClickReportABug}>
                <Flex>
                    <BugReportIcon />
                    <Typography fontSize={14}>Report a Bug</Typography>
                </Flex>
            </ListItemButton>
            <Divider />
            <ListItemButton onClick={handleLogout}>
                <Flex>
                    <LogOutIcon />
                    <Typography fontSize={14}>Logout</Typography>
                </Flex>
            </ListItemButton>
        </Menu>
    );
}
